import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type RefObject,
} from 'react';
import {
  Link,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import {
  ArrowUpRight,
  Disc3,
  Image as ImageIcon,
  Info,
  Pause,
  Play,
  Radio,
  Repeat,
  Repeat1,
  Search,
  Redo2,
  Shuffle,
  SkipBack,
  SkipForward,
  Undo2,
  X,
  type LucideIcon,
} from 'lucide-react';
import { Search as SearchIcon } from '@/components/animate-ui/icons/search';
import type { PhotoPost, Release, Track } from '@/types';
import { formatDuration } from '@/types';
import {
  artworkUrl,
  fetchMusicCatalog,
  fetchPhotos,
  flattenMusicCatalog,
  getCachedMusicReleases,
  getCachedPhotos,
  isPlayable,
  mediaUrl,
  preloadImageUrl,
  type SlimRelease,
} from '@/lib/api';
import { ScrollingTitle } from '@/components/ui/ScrollingTitle';
import { SeekRange } from '@/components/ui/SeekRange';
import {
  formatClock,
  formatRadioDelay,
} from '@/lib/radioSchedule';
import { parseAppPath, paths } from '@/lib/routes';
import {
  adjacentMode,
  modeIndex,
  modePath,
  modeSwitchDir,
  navigateViewTransition,
  runViewTransition,
  type ViewTransitionDir,
} from '@/lib/modeNav';
import { CatalogFormatLabel } from '@/components/list/CatalogFormatLabel';
import {
  MusicList,
  PhotoList,
  photoMatchesQuery,
} from '@/components/list/CatalogLists';
import { InfoPanel } from '@/components/list/InfoPanel';
import { SettingsPanel } from '@/components/list/SettingsPanel';
import { SearchSheet } from '@/components/list/SearchSheet';
import { MediaPanel } from '@/components/layout/MediaPanel';
import { MobileTabBar } from '@/components/layout/MobileTabBar';
import { PlayerProvider, usePlayer, type MenuMode } from '@/hooks/usePlayer';
import { PhotoCarouselProvider } from '@/hooks/usePhotoCarousel';
import { useCatalogRowFit } from '@/hooks/useCatalogRowFit';
import { SiteSearchProvider, useSiteSearch } from '@/hooks/useSiteSearch';
import {
  PreferencesProvider,
} from '@/hooks/usePreferences';
import type { VideoPlaylistItem } from '@/components/player/VideoOverlay';
import { resolveTrackArtist } from '@/lib/artist';
import { AUDIO_TAKES_OVER_EVENT } from '@/lib/videoSession';
import { NowPlayingTray } from '@/components/player/NowPlayingTray';
import { AudioFxSync } from '@/components/player/AudioFxSync';
import { useIsMobile } from '@/hooks/useIsMobile';

/** Desktop header nav only (settings is mobile-tab). */
const NAV_ITEMS: {
  key: Exclude<MenuMode, 'settings'>;
  label: string;
  to: string;
  Icon: LucideIcon;
}[] = [
  { key: 'music', label: 'Music', to: paths.music(), Icon: Disc3 },
  { key: 'photo', label: 'Photo', to: paths.photo(), Icon: ImageIcon },
  { key: 'info', label: 'Info', to: paths.info(), Icon: Info },
];

function yearsActive(): string {
  return `1985 - ${new Date().getFullYear()}`;
}

/**
 * Desktop footer art: keep the current cover painted until the next URL is
 * decoded, then crossfade — avoids a blank/glitchy flash on live track changes.
 */
function MiniPlayerArt({ src }: { src: string }) {
  const [baseSrc, setBaseSrc] = useState(src);
  const [fadeSrc, setFadeSrc] = useState<string | null>(null);
  const baseSrcRef = useRef(baseSrc);
  baseSrcRef.current = baseSrc;
  const fadeSrcRef = useRef(fadeSrc);
  fadeSrcRef.current = fadeSrc;
  const genRef = useRef(0);

  useEffect(() => {
    if (!src) {
      genRef.current += 1;
      setBaseSrc('');
      setFadeSrc(null);
      return;
    }
    if (src === baseSrcRef.current || src === fadeSrcRef.current) return;

    const gen = ++genRef.current;
    let cancelled = false;
    void (async () => {
      await preloadImageUrl(src, 4000);
      if (cancelled || gen !== genRef.current) return;
      if (src === baseSrcRef.current) return;
      const reduceMotion =
        typeof window !== 'undefined' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reduceMotion || !baseSrcRef.current) {
        setBaseSrc(src);
        setFadeSrc(null);
        return;
      }
      setFadeSrc(src);
    })();

    return () => {
      cancelled = true;
    };
  }, [src]);

  const commitFade = useCallback(() => {
    if (!fadeSrc) return;
    setBaseSrc(fadeSrc);
    setFadeSrc(null);
  }, [fadeSrc]);

  if (!baseSrc && !fadeSrc) {
    return <span className="mini-art-fallback" />;
  }

  return (
    <>
      {baseSrc ? (
        <img
          className="mini-art-layer"
          src={baseSrc}
          alt=""
          draggable={false}
          decoding="async"
        />
      ) : null}
      {fadeSrc ? (
        <img
          className="mini-art-layer is-incoming"
          src={fadeSrc}
          alt=""
          draggable={false}
          decoding="async"
          onAnimationEnd={commitFade}
        />
      ) : null}
    </>
  );
}

function SiteFooter() {
  const player = usePlayer();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const activeTrack = player.tracks[player.currentIndex];
  // Desktop only: session after first play. Mobile uses NowPlayingTray.
  // Keep chrome up while live is arming (radioMode) even if sessionActive
  // briefly lags — avoids unmounting the mini player on mode changes.
  const showPlayer = Boolean(
    !isMobile &&
      activeTrack &&
      player.tracks.length &&
      (player.sessionActive || player.radioMode),
  );

  const artSrc = artworkUrl(player.artwork);
  const duration = Math.max(1, activeTrack?.duration || 1);
  const radio = player.radioMode;
  const radioLive = radio && player.radioTimeshiftMs <= 0;
  const radioDelayLabel = radio
    ? radioLive
      ? 'LIVE'
      : formatRadioDelay(player.radioTimeshiftMs)
    : '';
  const clockTz = player.radioClockTimezone;
  const [radioClock, setRadioClock] = useState(() => formatClock());
  useEffect(() => {
    if (!showPlayer || !radio) return;
    const tick = () => setRadioClock(formatClock(Date.now(), clockTz));
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [showPlayer, radio, clockTz]);
  const artistName =
    activeTrack?.artist?.trim() ||
    player.artist?.trim() ||
    'Zachary Gray';
  const titleLine = activeTrack
    ? `${artistName} – ${activeTrack.name}`
    : '';
  /** Catalog release for the song on air (live) or the active album session. */
  const sourceReleaseId = (
    radio ? activeTrack?.releaseId : player.releaseId
  )?.trim() || '';
  const openPlayingRelease = useCallback(() => {
    if (!sourceReleaseId) return;
    navigateViewTransition(
      navigate,
      paths.musicRelease(sourceReleaseId),
      'forward',
    );
  }, [navigate, sourceReleaseId]);
  const playableCount = player.tracks.filter(isPlayable).length;
  const canShuffle = !radio && playableCount >= 2;
  const canRepeat = !radio;
  const repeatLabel =
    player.repeatMode === 'one'
      ? 'Repeat one'
      : player.repeatMode === 'all'
        ? 'Repeat all'
        : 'Repeat off';

  return (
    <footer
      className={`site-footer${showPlayer ? ' site-footer--player' : ''}`}
    >
      <span className="location" aria-hidden={showPlayer || undefined}>
        <span className="location-flag" aria-hidden />
        Based in Newfoundland.
      </span>
      {showPlayer && activeTrack ? (
        <div
          className={`mini-player${radio ? ' is-live' : ''}${radio && !radioLive ? ' is-timeshift' : ''}`}
        >
          <div className="mini-left">
            <button
              type="button"
              className="mini-now-playing"
              onClick={openPlayingRelease}
              disabled={!sourceReleaseId}
              aria-label={
                sourceReleaseId
                  ? `Open release for ${activeTrack.name}`
                  : undefined
              }
              title={sourceReleaseId ? 'Open release' : undefined}
            >
              <span className="mini-art" aria-hidden>
                <MiniPlayerArt src={artSrc} />
              </span>
              <ScrollingTitle
                text={titleLine}
                className="playing-title"
                resetOnViewTransition={false}
              />
            </button>
            <div className="mini-transport">
              <button
                type="button"
                className="mini-transport-btn"
                aria-label={radio ? 'Skip back 15 seconds' : 'Previous track'}
                title={radio ? 'Back 15s' : 'Previous track'}
                onClick={player.playPrevious}
              >
                {radio ? (
                  <Undo2 size={14} strokeWidth={2} />
                ) : (
                  <SkipBack size={14} />
                )}
              </button>
              <button
                type="button"
                className="mini-transport-btn mini-play"
                aria-label={
                  player.playing ? 'Pause playback' : 'Resume playback'
                }
                title="Play / Pause"
                onClick={() => player.toggleTrack(activeTrack.position)}
              >
                {player.playing ? (
                  <Pause size={14} fill="currentColor" />
                ) : (
                  <Play size={14} fill="currentColor" />
                )}
              </button>
              <button
                type="button"
                className="mini-transport-btn"
                aria-label={
                  radio
                    ? radioLive
                      ? 'Already live'
                      : 'Skip forward 15 seconds toward live'
                    : 'Next track'
                }
                title={
                  radio ? (radioLive ? 'Live' : 'Forward 15s') : 'Next track'
                }
                disabled={radio && radioLive}
                onClick={player.playNext}
              >
                {radio ? (
                  <Redo2 size={14} strokeWidth={2} />
                ) : (
                  <SkipForward size={14} />
                )}
              </button>
            </div>
          </div>
          <div className="mini-right">
            <div className="mini-timeline">
              {radio ? (
                <>
                  <span
                    className="player-time"
                    title={clockTz}
                    aria-label={`Station time ${radioClock}`}
                  >
                    {radioClock}
                  </span>
                  <div
                    className={[
                      'live-progress live-progress-mini',
                      player.radioBuffering
                        ? 'is-buffering'
                        : player.playing && radioLive
                          ? 'is-playing'
                          : '',
                      !player.radioBuffering && !radioLive
                        ? 'is-timeshift'
                        : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    role="status"
                    aria-label={
                      player.radioBuffering
                        ? 'Buffering live radio'
                        : radioLive
                          ? 'Live'
                          : `Behind live ${radioDelayLabel}`
                    }
                    aria-busy={player.radioBuffering || undefined}
                  >
                    <div className="live-progress-track">
                      <div className="live-progress-bar" aria-hidden />
                      <button
                        type="button"
                        className={`live-progress-live${
                          player.radioBuffering
                            ? ' is-buffering'
                            : radioLive
                              ? ''
                              : ' is-behind'
                        }`}
                        aria-label={
                          player.radioBuffering
                            ? 'Buffering'
                            : radioLive
                              ? 'On live'
                              : 'Jump to live'
                        }
                        disabled={radioLive || player.radioBuffering}
                        onClick={() => player.jumpToLive()}
                      >
                        <span
                          className="live-progress-live-text"
                          key={player.radioBuffering ? 'buf' : 'live'}
                        >
                          {player.radioBuffering ? 'BUFFERING' : 'LIVE'}
                        </span>
                      </button>
                    </div>
                  </div>
                  <span
                    className="player-time player-duration"
                    aria-label="End of day"
                    title="End of day"
                  >
                    24:00
                  </span>
                </>
              ) : (
                <>
                  <span className="player-time">
                    {formatDuration(player.currentTime)}
                  </span>
                  <SeekRange
                    className="seek"
                    aria-label="Seek audio"
                    min={0}
                    max={duration}
                    step={0.1}
                    value={player.currentTime}
                    liveSeek={false}
                    onScrubbingChange={player.setSeekScrubbing}
                    onSeek={player.seek}
                  />
                  <span className="player-time player-duration">
                    -
                    {formatDuration(
                      Math.max(0, duration - player.currentTime),
                    )}
                  </span>
                </>
              )}
            </div>
            <div className="mini-modes">
              <button
                type="button"
                className={`mini-transport-btn${player.shuffleMode && canShuffle ? ' is-active' : ''}`}
                aria-label={
                  radio
                    ? 'Shuffle unavailable in live mode'
                    : canShuffle
                      ? player.shuffleMode
                        ? 'Shuffle on'
                        : 'Shuffle off'
                      : 'Shuffle unavailable'
                }
                aria-pressed={player.shuffleMode && canShuffle}
                title={
                  radio
                    ? 'Unavailable live'
                    : canShuffle
                      ? 'Shuffle'
                      : 'Not enough tracks to shuffle'
                }
                disabled={!canShuffle}
                onClick={player.toggleShuffle}
              >
                <Shuffle size={14} />
              </button>
              <button
                type="button"
                className={`mini-transport-btn${player.repeatMode !== 'off' && canRepeat ? ' is-active' : ''}`}
                aria-label={
                  radio ? 'Repeat unavailable in live mode' : repeatLabel
                }
                aria-pressed={player.repeatMode !== 'off' && canRepeat}
                title={radio ? 'Unavailable live' : repeatLabel}
                disabled={!canRepeat}
                onClick={player.cycleRepeat}
              >
                {player.repeatMode === 'one' ? (
                  <Repeat1 size={14} />
                ) : (
                  <Repeat size={14} />
                )}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      <span className="footer-years" aria-hidden={showPlayer || undefined}>
        {yearsActive()}
      </span>
    </footer>
  );
}

function HeaderSearch({
  placeholder,
  inputRef,
  expanded = true,
  onExpand,
  onCollapse,
}: {
  placeholder: string;
  inputRef?: RefObject<HTMLInputElement | null>;
  /** Desktop icon→field: when false, only the icon is interactive */
  expanded?: boolean;
  onExpand?: () => void;
  onCollapse?: () => void;
}) {
  const { query, setQuery, clearQuery } = useSiteSearch();

  return (
    <label
      className="header-search"
      htmlFor="site-search"
      onClick={(e) => {
        if (expanded) return;
        e.preventDefault();
        onExpand?.();
      }}
    >
      <Search size={16} strokeWidth={1.75} aria-hidden />
      <input
        ref={inputRef}
        id="site-search"
        type="search"
        enterKeyHint="search"
        placeholder={placeholder}
        aria-label="Search site"
        tabIndex={expanded ? 0 : -1}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onBlur={(e) => {
          const next = e.relatedTarget as Node | null;
          const root = e.currentTarget.closest('.header-search');
          if (next && root?.contains(next)) return;
          if (!query.trim()) onCollapse?.();
        }}
      />
      {query ? (
        <button
          type="button"
          className="header-search-clear"
          aria-label="Clear search"
          tabIndex={expanded ? 0 : -1}
          onMouseDown={(e) => e.preventDefault()}
          onClick={(e) => {
            e.preventDefault();
            clearQuery();
            onCollapse?.();
          }}
        >
          <X size={14} />
        </button>
      ) : null}
    </label>
  );
}

function MenuSearchResults({
  open,
  onPick,
}: {
  open: boolean;
  onPick: () => void;
}) {
  const { query } = useSiteSearch();
  const [music, setMusic] = useState<SlimRelease[]>(
    () => getCachedMusicReleases() ?? [],
  );
  const [photos, setPhotos] = useState<PhotoPost[]>(
    () => getCachedPhotos() ?? [],
  );
  const [loading, setLoading] = useState(
    () => getCachedMusicReleases() == null || getCachedPhotos() == null,
  );
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const cachedMusic = getCachedMusicReleases();
    const cachedPhotos = getCachedPhotos();
    if (cachedMusic && cachedPhotos) {
      setMusic(cachedMusic);
      setPhotos(cachedPhotos);
      setLoading(false);
      return;
    }
    setLoading(true);
    (async () => {
      try {
        const [photoList, catalog] = await Promise.all([
          fetchPhotos(),
          fetchMusicCatalog(),
        ]);
        if (cancelled) return;
        setPhotos(photoList);
        setMusic(flattenMusicCatalog(catalog));
      } catch (e) {
        console.error(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  const q = query.trim().toLowerCase();

  const musicHits = useMemo(() => {
    if (!q) return [];
    return music.filter((r) => r.name.toLowerCase().includes(q));
  }, [music, q]);

  const photoHits = useMemo(() => {
    if (!q) return [];
    return photos.filter((p) => photoMatchesQuery(p, q));
  }, [photos, q]);

  const hitCount = musicHits.length + photoHits.length;
  const hitsReady = Boolean(q) && !loading;

  useCatalogRowFit(scrollRef, hitCount, open && hitsReady && hitCount > 0);
  const navigate = useNavigate();

  return (
    <div
      ref={scrollRef}
      className={`menu-search-results${hitsReady ? ' has-query' : ''}`}
      aria-hidden={!open}
      aria-label="Site search results"
    >
      {hitsReady && hitCount === 0 ? (
        <p className="menu-search-empty">No results.</p>
      ) : null}
      {hitsReady && musicHits.length ? (
        <>
          <p className="menu-search-section">Music</p>
          {musicHits.map((item, i) => {
            const year = new Date(item.releaseDate).getFullYear();
            return (
              <Link
                key={`m-${item.id}`}
                to={paths.musicRelease(item.id)}
                className="catalog-row menu-search-row"
                style={{ '--result-i': i } as CSSProperties}
                onClick={(e) => {
                  e.preventDefault();
                  onPick();
                  runViewTransition(() => {
                    navigate(paths.musicRelease(item.id));
                  }, 'forward');
                }}
              >
                <span className="row-year">{year}</span>
                <span className="row-title">{item.name}</span>
                <CatalogFormatLabel label={item.type} />
                <ArrowUpRight size={16} />
              </Link>
            );
          })}
        </>
      ) : null}
      {hitsReady && photoHits.length ? (
        <>
          <p className="menu-search-section">Photo</p>
          {photoHits.map((item, i) => {
            const year = new Date(item.date).getFullYear();
            return (
              <Link
                key={`p-${item.id}`}
                to={paths.photoPost(item.id)}
                className="catalog-row menu-search-row"
                style={
                  {
                    '--result-i': musicHits.length + i,
                  } as CSSProperties
                }
                onClick={(e) => {
                  e.preventDefault();
                  onPick();
                  runViewTransition(() => {
                    navigate(paths.photoPost(item.id));
                  }, 'forward');
                }}
              >
                <span className="row-year">{year}</span>
                <span className="row-title">{item.title}</span>
                <CatalogFormatLabel label={item.format} />
                <ArrowUpRight size={16} />
              </Link>
            );
          })}
        </>
      ) : null}
    </div>
  );
}

function SiteHeader({
  mode,
  onSwitch,
  searchOpen,
  setSearchOpen,
}: {
  mode: MenuMode;
  onSwitch: (next: MenuMode, dir?: ViewTransitionDir) => void;
  searchOpen: boolean;
  setSearchOpen: (open: boolean) => void;
}) {
  const isMobile = useIsMobile();
  const { startRadio, radioMode, radioBuffering, playing } = usePlayer();
  const { query, clearQuery } = useSiteSearch();
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [desktopSearchOpen, setDesktopSearchOpen] = useState(false);
  const hasQuery = query.trim().length > 0;
  const desktopSearchExpanded = desktopSearchOpen || hasQuery;

  useEffect(() => {
    clearQuery();
    setDesktopSearchOpen(false);
    setSearchOpen(false);
  }, [mode, clearQuery, setSearchOpen]);

  useEffect(() => {
    if (!isMobile) setSearchOpen(false);
    else setDesktopSearchOpen(false);
  }, [isMobile, setSearchOpen]);

  useEffect(() => {
    if (isMobile || !desktopSearchExpanded) return;
    const id = window.setTimeout(() => searchInputRef.current?.focus(), 40);
    return () => window.clearTimeout(id);
  }, [isMobile, desktopSearchExpanded]);

  useEffect(() => {
    if (isMobile || !(hasQuery || desktopSearchOpen)) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      clearQuery();
      setDesktopSearchOpen(false);
    };
    const onPointer = (e: PointerEvent) => {
      const target = e.target as Node | null;
      if (
        desktopSearchOpen &&
        !hasQuery &&
        target &&
        !(target as Element).closest?.('.header-search-desktop')
      ) {
        setDesktopSearchOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('pointerdown', onPointer);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('pointerdown', onPointer);
    };
  }, [clearQuery, isMobile, hasQuery, desktopSearchOpen]);

  const headerClass = [
    'site-header',
    searchOpen ? 'search-sheet-open' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <header className={headerClass}>
      <Link
        to={paths.music()}
        className="identity"
        onClick={(e) => {
          clearQuery();
          setSearchOpen(false);
          if (mode === 'music') return;
          e.preventDefault();
          onSwitch('music');
        }}
      >
        <img
          className="identity-mark"
          src="https://media.thisiszacharygray.com/img/favicon.svg"
          alt=""
          width={22}
          height={22}
        />
        <span>This is Zachary Gray.</span>
      </Link>

      <nav className="site-nav-desktop" aria-label="Main navigation">
        {NAV_ITEMS.map(({ key, label, to }) => (
          <Link
            key={key}
            to={to}
            className={`nav-item${mode === key ? ' nav-active' : ''}`}
            onClick={(e) => {
              e.preventDefault();
              onSwitch(key);
            }}
          >
            {label}
          </Link>
        ))}
      </nav>

      {!isMobile ? (
        <div className="header-end-desktop">
          <button
            type="button"
            className={`header-live-btn${radioBuffering ? ' is-buffering' : radioMode && playing ? ' is-live' : ''}`}
            aria-label={
              radioBuffering
                ? 'Connecting to live radio'
                : radioMode && playing
                  ? 'Live radio playing'
                  : 'Listen to live radio'
            }
            aria-pressed={radioMode}
            aria-busy={radioBuffering || undefined}
            title="Live radio"
            onClick={() => void startRadio()}
          >
            <Radio size={18} strokeWidth={1.5} aria-hidden />
          </button>
          <div
            className={`header-search-slot header-search-desktop${desktopSearchExpanded ? ' is-expanded' : ''}`}
          >
            <HeaderSearch
              placeholder="Search"
              inputRef={searchInputRef}
              expanded={desktopSearchExpanded}
              onExpand={() => setDesktopSearchOpen(true)}
              onCollapse={() => setDesktopSearchOpen(false)}
            />
          </div>
        </div>
      ) : (
        <div className="header-actions-mobile">
          <button
            type="button"
            className={`header-live-btn${radioBuffering ? ' is-buffering' : radioMode && playing ? ' is-live' : ''}`}
            aria-label={
              radioBuffering
                ? 'Connecting to live radio'
                : radioMode && playing
                  ? 'Live radio playing'
                  : 'Listen to live radio'
            }
            aria-pressed={radioMode}
            aria-busy={radioBuffering || undefined}
            onClick={() => void startRadio()}
          >
            <Radio size={15} strokeWidth={2} aria-hidden />
          </button>
          <button
            type="button"
            className={`menu-toggle search-toggle${searchOpen ? ' is-active' : ''}`}
            aria-expanded={searchOpen}
            aria-controls="mobile-search-sheet"
            aria-label={searchOpen ? 'Close search' : 'Search'}
            onClick={() => setSearchOpen(!searchOpen)}
          >
            <SearchIcon
              size={15}
              strokeWidth={2}
              animate={searchOpen}
              aria-hidden
            />
          </button>
        </div>
      )}

    </header>
  );
}

function AppShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const player = usePlayer();
  const isMobile = useIsMobile();
  const { query, clearQuery } = useSiteSearch();
  const { mode, id } = parseAppPath(location.pathname);
  const desktopSearchActive = !isMobile && query.trim().length > 0;

  const [release, setRelease] = useState<Release | null>(null);
  const [photo, setPhoto] = useState<PhotoPost | null>(null);
  const [video, setVideo] = useState<VideoPlaylistItem | null>(null);
  /** Snapshot at play time — survives leaving the release / switching tabs. */
  const [videoSessionPlaylist, setVideoSessionPlaylist] = useState<
    VideoPlaylistItem[]
  >([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const workspaceRef = useRef<HTMLElement>(null);
  const swipeRef = useRef<{
    x: number;
    y: number;
    active: boolean;
    axis: 'none' | 'x' | 'y';
  } | null>(null);

  const detail = Boolean(id && mode !== 'info' && mode !== 'settings');
  const sessionLive = Boolean(
    player.tracks[player.currentIndex] &&
      (player.sessionActive || player.radioMode),
  );
  const playerChrome = !sessionLive
    ? 'none'
    : isMobile
      ? 'tray'
      : 'footer';
  const showTray = playerChrome === 'tray';
  const liveDeepLinkConsumed = useRef(false);
  const startRadio = player.startRadio;

  // Share deep-link: `/music?live=1` → start shared live radio once.
  useEffect(() => {
    if (mode !== 'music' || id) return;
    const live = new URLSearchParams(location.search).get('live');
    if (live !== '1' || liveDeepLinkConsumed.current) return;
    liveDeepLinkConsumed.current = true;
    void startRadio();
    navigate(paths.music(), { replace: true });
  }, [mode, id, location.search, navigate, startRadio]);

  useEffect(() => {
    // Clear detail shells for the inactive mode — video session is independent
    // (keeps playing in the mini tray like audio).
    if (mode !== 'music') {
      setRelease(null);
    } else if (!id) {
      setRelease(null);
    }
    if (mode !== 'photo') {
      setPhoto(null);
    } else if (!id) {
      setPhoto(null);
    }
  }, [mode, id]);

  // Open release / photo detail at the top (avoid residual scroll / snap position).
  useEffect(() => {
    if (!detail) return;
    const el = workspaceRef.current;
    if (!el) return;
    el.scrollTop = 0;
  }, [detail, id]);

  const switchMode = (next: MenuMode, dir?: ViewTransitionDir) => {
    if (next === mode && !id) {
      setSearchOpen(false);
      return;
    }
    setSearchOpen(false);
    // List↔list mode tabs: mode-* keeps the shared portrait still.
    // Leaving a release/photo detail: forward/back so artwork↔photo crossfades.
    const leavingDetail = Boolean(id);
    const resolved: ViewTransitionDir | undefined =
      dir ??
      (next === mode
        ? undefined
        : leavingDetail
          ? modeIndex(next) > modeIndex(mode)
            ? 'forward'
            : 'back'
          : modeSwitchDir(mode, next));

    runViewTransition(() => {
      navigate(modePath(next));
    }, resolved);
  };

  /* Horizontal swipe between music ↔ photo ↔ info ↔ settings */
  useEffect(() => {
    if (!isMobile) return;
    const el = workspaceRef.current;
    if (!el) return;

    // Video in mini tray should not block mode swipes (same as audio).
    const blocked = () => Boolean(searchOpen || detail);

    const onStart = (e: TouchEvent) => {
      if (blocked()) {
        swipeRef.current = null;
        return;
      }
      const target = e.target as HTMLElement | null;
      // Don't capture horizontal swipes that start on sliders / interactive chrome.
      if (
        target?.closest?.(
          'input, textarea, select, button, a, [role="slider"], [data-no-swipe]',
        )
      ) {
        swipeRef.current = null;
        return;
      }
      const t = e.touches[0];
      if (!t) return;
      swipeRef.current = {
        x: t.clientX,
        y: t.clientY,
        active: true,
        axis: 'none',
      };
    };

    const onMove = (e: TouchEvent) => {
      const start = swipeRef.current;
      if (!start?.active || blocked()) return;
      const t = e.touches[0];
      if (!t) return;
      const dx = t.clientX - start.x;
      const dy = t.clientY - start.y;
      if (start.axis === 'none') {
        if (Math.abs(dx) < 10 && Math.abs(dy) < 10) return;
        start.axis = Math.abs(dx) > Math.abs(dy) * 1.1 ? 'x' : 'y';
      }
      if (start.axis === 'x' && e.cancelable) {
        e.preventDefault();
      }
    };

    const onEnd = (e: TouchEvent) => {
      const start = swipeRef.current;
      swipeRef.current = null;
      if (!start?.active || start.axis !== 'x' || blocked()) return;
      const t = e.changedTouches[0];
      if (!t) return;
      const dx = t.clientX - start.x;
      if (Math.abs(dx) < 48) return;
      const next = adjacentMode(mode, dx < 0 ? 1 : -1);
      if (next) switchMode(next);
    };

    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd, { passive: true });
    el.addEventListener('touchcancel', onEnd, { passive: true });
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onEnd);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, searchOpen, detail, video, isMobile]);

  /** Live playlist from the open release (desktop overlay / shelf). */
  const videoPlaylist = useMemo<VideoPlaylistItem[]>(() => {
    if (!release?.tracks?.length) return [];
    const art = release.artworkUrl || undefined;
    const releaseArtist = release.artist?.trim() || undefined;
    return release.tracks
      .filter((t) => Boolean(t.video))
      .map((t) => {
        const base = String(t.video || '').replace(/\/+$/, '');
        const poster = base ? `${base}/poster.jpg` : art;
        return {
          url: mediaUrl(t.video!),
          track: t,
          artwork: art,
          poster: poster || art,
          album: release.name,
          // Prefer track artist; skip album-level "Various Artists".
          artist: resolveTrackArtist(t.artist, releaseArtist),
        };
      });
  }, [release]);

  const onVideo = useCallback(
    (url: string, track: Track) => {
      try {
        player.audioRef.current?.pause();
      } catch {
        /* ignore */
      }
      const art = release?.artworkUrl || player.artwork || undefined;
      const base = String(track.video || '').replace(/\/+$/, '');
      const poster = base ? `${base}/poster.jpg` : art;
      const item: VideoPlaylistItem = {
        url,
        track,
        artwork: art,
        poster: poster || art,
        album: release?.name,
        artist: resolveTrackArtist(track.artist, release?.artist),
      };
      // Snapshot queue + artwork so leaving the release doesn't tear down playback.
      const snapshot =
        videoPlaylist.length > 0
          ? videoPlaylist
          : [item];
      setVideoSessionPlaylist(snapshot);
      setVideo(item);
    },
    [
      player.audioRef,
      player.artwork,
      release?.artworkUrl,
      release?.artist,
      release?.name,
      videoPlaylist,
    ],
  );
  const onSelectVideo = useCallback((item: VideoPlaylistItem) => {
    setVideo(item);
    setVideoSessionPlaylist((prev) => (prev.length ? prev : [item]));
  }, []);
  const onCloseVideo = useCallback(() => {
    setVideo(null);
    setVideoSessionPlaylist([]);
  }, []);

  // Track row / radio / album play must replace video — not layer under it.
  useEffect(() => {
    const onAudioTakesOver = () => {
      setVideo(null);
      setVideoSessionPlaylist([]);
    };
    window.addEventListener(AUDIO_TAKES_OVER_EVENT, onAudioTakesOver);
    return () => {
      window.removeEventListener(AUDIO_TAKES_OVER_EVENT, onAudioTakesOver);
    };
  }, []);

  const onSelectRelease = useCallback((next: Release | null) => {
    setRelease(next);
  }, []);

  const onSelectPhoto = useCallback((next: PhotoPost | null) => {
    setPhoto(next);
  }, []);

  const left = useMemo(() => {
    if (mode === 'music') {
      return (
        <MusicList
          selectedId={id}
          onSelectRelease={onSelectRelease}
          onVideo={onVideo}
          activeVideoUrl={video?.url}
        />
      );
    }
    if (mode === 'photo') {
      return <PhotoList selectedId={id} onSelectPhoto={onSelectPhoto} />;
    }
    if (mode === 'settings') {
      return <SettingsPanel />;
    }
    return <InfoPanel />;
  }, [mode, id, video?.url, onSelectRelease, onSelectPhoto, onVideo]);

  const workspaceClass = [
    'workspace',
    detail ? 'has-detail' : '',
    mode === 'info' ? 'is-info' : '',
    mode === 'settings' ? 'is-settings' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={`portfolio${isMobile ? ' has-mobile-tabs' : ''}`}
      data-player-chrome={playerChrome}
    >
      <SiteHeader
        mode={mode}
        onSwitch={switchMode}
        searchOpen={searchOpen}
        setSearchOpen={setSearchOpen}
      />

      <main ref={workspaceRef} className={workspaceClass}>
        <section
          className={`index-panel${desktopSearchActive ? ' is-searching' : ''}`}
          aria-label={
            desktopSearchActive
              ? 'Site search results'
              : mode === 'info'
                ? 'Artist information'
                : mode === 'settings'
                  ? 'Settings'
                  : `${mode} catalog`
          }
        >
          <div
            className="index-panel-catalog"
            aria-hidden={desktopSearchActive}
          >
            {left}
          </div>
          {!isMobile ? (
            <MenuSearchResults
              open={desktopSearchActive}
              onPick={() => clearQuery()}
            />
          ) : null}
        </section>
        <MediaPanel
          mode={mode}
          release={release}
          photo={photo}
          detail={detail}
          video={isMobile ? null : video}
          videoPlaylist={videoPlaylist}
          onSelectVideo={onSelectVideo}
          onCloseVideo={onCloseVideo}
        />
      </main>

      <NowPlayingTray
        visible={showTray || Boolean(isMobile && video)}
        video={isMobile ? video : null}
        videoPlaylist={
          videoSessionPlaylist.length ? videoSessionPlaylist : videoPlaylist
        }
        onSelectVideo={onSelectVideo}
        onCloseVideo={onCloseVideo}
      />
      {isMobile ? (
        <MobileTabBar mode={mode} onSwitch={(m) => switchMode(m)} />
      ) : (
        <SiteFooter />
      )}
      {isMobile ? (
        <SearchSheet open={searchOpen} onClose={() => setSearchOpen(false)} />
      ) : null}
    </div>
  );
}

export default function App() {
  return (
    <PreferencesProvider>
      <PlayerProvider>
        <AudioFxSync />
        <SiteSearchProvider>
          <PhotoCarouselProvider>
            <Routes>
              <Route path="/" element={<Navigate to={paths.music()} replace />} />
              {/* Single shell instance so portrait / gradient keep running across views */}
              <Route path="*" element={<AppShell />} />
            </Routes>
          </PhotoCarouselProvider>
        </SiteSearchProvider>
      </PlayerProvider>
    </PreferencesProvider>
  );
}
